/**
 * Shared helpers for the Quality & Client Care generators.
 *
 * Every generator builds a deterministic, record-level history (incidents,
 * doses, goals, audits) from DATA_START to now, then aggregates it for the
 * requested scope. Quarter numbers therefore always add up to the all-time
 * numbers, and the current quarter grows naturally as the days pass.
 */
import { DATA_START, Quarter, addDays, iso, now, ym, ymd } from '../../clock';
import { Rng } from '../../rng';
import { lastSyncedAt } from '../../state';
import { CLIENTS, Client, STAFF, Staff, activeClients } from '../../world';

export const DAY = 86400000;

/** Inclusive date window a stats call covers. `to` never runs past now. */
export interface Scope {
    q: Quarter | null;
    from: Date;
    to: Date;
    months: string[];
}

export function scopeOf(q: Quarter | null): Scope {
    const n = now();
    if (!q) {
        return { q: null, from: new Date(DATA_START), to: n, months: monthsBetween(DATA_START, n) };
    }
    const to = q.end.getTime() < n.getTime() ? q.end : n;
    const from = q.start.getTime() < DATA_START.getTime() ? new Date(DATA_START) : q.start;
    return { q, from, to, months: to < from ? [] : monthsBetween(from, to) };
}

/**
 * Window for a page's monthly trend chart: the twelve months up to the end of
 * the selected scope (never before DATA_START). A single quarter is at most
 * three points (one, early in a quarter), which reads as an empty chart, so
 * the trend always carries the preceding months for context.
 */
export function trendScopeOf(q: Quarter | null): Scope {
    const s = scopeOf(q);
    const back = new Date(s.to.getFullYear(), s.to.getMonth() - 11, 1);
    const from = back.getTime() < DATA_START.getTime() ? new Date(DATA_START) : back;
    return { q, from, to: s.to, months: monthsBetween(from, s.to) };
}

export function inScope(d: Date, s: Scope): boolean {
    return d.getTime() >= s.from.getTime() && d.getTime() <= s.to.getTime();
}

export function monthsBetween(from: Date, to: Date): string[] {
    const out: string[] = [];
    for (let d = new Date(from.getFullYear(), from.getMonth(), 1); d <= to; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
        out.push(ym(d));
    }
    return out;
}

/** Every month of data, DATA_START .. current month. */
export function historyMonths(): string[] {
    return monthsBetween(DATA_START, now());
}

export function monthStart(m: string): Date {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1);
}

export function daysInMonth(m: string): number {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo, 0).getDate();
}

/** A date inside month `m` on `day` at hh:mm. */
export function dateIn(m: string, day: number, hour = 9, minute = 0): Date {
    const s = monthStart(m);
    return new Date(s.getFullYear(), s.getMonth(), Math.min(day, daysInMonth(m)), hour, minute);
}

export function daysBetween(a: Date, b: Date): number {
    return Math.floor((b.getTime() - a.getTime()) / DAY);
}

/** Start of today (local). */
export function today(): Date {
    const n = now();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

export function round1(v: number): number {
    return Math.round(v * 10) / 10;
}

export function round4(v: number): number {
    return Math.round(v * 10000) / 10000;
}

/**
 * Split `total` into integer parts proportional to `weights` (largest
 * remainder), so the parts always sum to exactly `total`.
 */
export function allocate(total: number, weights: number[]): number[] {
    const sum = weights.reduce((a, b) => a + b, 0) || 1;
    const raw = weights.map(w => (total * w) / sum);
    const out = raw.map(Math.floor);
    let left = total - out.reduce((a, b) => a + b, 0);
    const order = raw.map((r, i) => [r - Math.floor(r), i] as [number, number]).sort((a, b) => b[0] - a[0]);
    for (let k = 0; left > 0 && order.length; k = (k + 1) % order.length, left--) {
        out[order[k][1]]++;
    }
    return out;
}

/** Weighted pick of a client, favouring a few "frequent" clients like real incident data. */
export function pickClient(r: Rng, pool: Client[], hot: Client[], hotShare = 0.3): Client {
    return hot.length && r.bool(hotShare) ? r.pick(hot) : r.pick(pool);
}

/** Active direct-care staff for a program, preferring those based at `site`. */
export function careStaff(program: string, site: string): Staff[] {
    const active = STAFF.filter(s => !s.endDate && s.program === program);
    const local = active.filter(s => s.site === site);
    return local.length ? local : active.length ? active : STAFF.filter(s => !s.endDate);
}

export function clientByCode(code: string): Client | undefined {
    return CLIENTS.find(c => c.code === code);
}

export { activeClients, iso, ymd, addDays, now };

/** `last_synced_at` for a Webcare/Tendable-backed stats payload. */
export function syncedAt(type: string): string {
    return lastSyncedAt(type);
}

/** Sort helper: count desc, then label asc. */
export function byCountDesc<T>(label: (t: T) => string, count: (t: T) => number) {
    return (a: T, b: T): number => count(b) - count(a) || label(a).localeCompare(label(b));
}

export function tally<T>(items: T[], key: (t: T) => string): Map<string, number> {
    const m = new Map<string, number>();
    for (const it of items) {
        const k = key(it);
        m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
}

/** Memoise a builder per key (the key folds in today's date so "live" data refreshes daily). */
export function memo<T>(build: (key: string) => T): (key: string) => T {
    const cache = new Map<string, T>();
    return (key: string) => {
        const k = `${key}|${ymd(now())}`;
        if (!cache.has(k)) {
            cache.set(k, build(key));
        }
        return cache.get(k) as T;
    };
}
